// hooks/useFloorPlanState.js
import { yupResolver } from "@hookform/resolvers/yup";
import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import FocusIcon from "src/assets/icons/floor-plan/FocusIcon";
import PointerIcon from "src/assets/icons/floor-plan/PointerIcon";
import TextIcon from "src/assets/icons/floor-plan/TextIcon";
import TransformIcon from "src/assets/icons/floor-plan/TransformIcon";
import ZoomInIcon from "src/assets/icons/floor-plan/ZoomInIcon";
import ZoomOutIcon from "src/assets/icons/floor-plan/ZoomOutIcon";
import {
  clearSelectTransform,
  handleReCenter,
  handleZoomIn,
  handleZoomOut,
} from "src/utils/canvasHandlers";
import ImageIcon from "src/assets/icons/floor-plan/ImageIcon";
import { useSnackbar } from "notistack";
import { useLocales } from "src/locales";

const useFloorPlanState = ({
  getEventById,
  getFloorPlans,
  upsertFloorPlans,
  listingId,
  stageSize,
}) => {
  const [booking, setBooking] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [tablesInventory, setTablesInventory] = useState({});
  const [floorPlans, setFloorPlans] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [mode, setMode] = useState("select");

  const [customerTablesInventory, setCustomerTablesInventory] = useState({});
  const [customerAreasInventory, setCustomerAreasInventory] = useState({});
  const [customerPlans, setCustomerPlans] = useState({});

  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();

  const shapeRefs = useRef({});
  const layerRef = useRef(null);
  const transformerRef = useRef(null);
  const imageTransformerRef = useRef(null);
  const stageRef = useRef(null);

  const fileInputRef = useRef(null);

  const defaultValues = {
    managementSection: true,
    viewportMobile: true,
    enabledCustomerPlan: false
  };

  const SectionSchema = Yup.object().shape({
    managementSection: Yup.boolean(),
  });

  const ViewportSchema = Yup.object().shape({
    viewportMobile: Yup.boolean(),
  });

  const EnabledPlanSchema = Yup.object().shape({
    enabledCustomerPlan: Yup.boolean(),
  });

  const methods = useForm({
    resolver: yupResolver(SectionSchema),
    defaultValues,
  });

  const viewportMethods = useForm({
    resolver: yupResolver(ViewportSchema),
    defaultValues,
  });

  const enabledPlanMethods = useForm({
    resolver: yupResolver(EnabledPlanSchema),
    defaultValues,
  });

  const { watch } = methods;
  const { watch: viewportWatch } = viewportMethods;
  const { watch: enabledPlanWatch } = enabledPlanMethods;

  const isManagementSection = watch("managementSection");
  const isViewportMobile = viewportWatch("viewportMobile");
  const isEnabledCustomerPlan = enabledPlanWatch("enabledCustomerPlan")

  const setInventory = isManagementSection
    ? setTablesInventory
    : setCustomerTablesInventory;
  const setPlans = isManagementSection ? setFloorPlans : setCustomerPlans;

  const managementActions = [
    {
      actionName: "Re-center",
      icon: <FocusIcon />,
      doAction: () => handleReCenter(stageRef),
    },
    {
      actionName: "Zoom Out",
      icon: <ZoomOutIcon />,
      doAction: () => handleZoomOut(stageRef),
    },
    {
      actionName: "Zoom In",
      icon: <ZoomInIcon />,
      doAction: () => handleZoomIn(stageRef),
    },
    {
      actionName: "Text",
      icon: <TextIcon />,
      doAction: () => {
        clearSelectTransform(setSelectedIds, transformerRef);
        setMode("text");
      },
    },
    {
      actionName: "Draw",
      icon: <TransformIcon />,
      doAction: () => {
        clearSelectTransform(setSelectedIds, transformerRef);
        setMode("draw");
      },
    },
    {
      actionName: "Select",
      icon: <PointerIcon />,
      doAction: () => setMode("select"),
    },
  ];

  const customerActions = [
    {
      actionName: "Re-center",
      icon: <FocusIcon />,
      doAction: () => handleReCenter(stageRef),
    },
    {
      actionName: "Zoom Out",
      icon: <ZoomOutIcon />,
      doAction: () => handleZoomOut(stageRef),
    },
    {
      actionName: "Zoom In",
      icon: <ZoomInIcon />,
      doAction: () => handleZoomIn(stageRef),
    },
    {
      actionName: "Image",
      icon: <ImageIcon />,
      doAction: () =>
        !customerPlans[selectedArea?.id]?.find(
          (shape) => shape.type === "image"
        ) && fileInputRef.current.click(),
    },
    {
      actionName: "Select",
      icon: <PointerIcon />,
      doAction: () => setMode("select"),
    },
  ];

  const canvasActions = isManagementSection
    ? managementActions
    : customerActions;

  useEffect(() => {
    const fetch = async () => {
      const dataEvent = await getEventById({
        variables: { eventId: listingId },
      });
      if (dataEvent?.data) {
        const bookingData = dataEvent.data.event.booking;
        const dataFloorPlans = await getFloorPlans({
          variables: { bookingModuleId: bookingData.id },
        });
        const floorPlansData = dataFloorPlans?.data?.floorPlan;

        enabledPlanMethods.reset({
          enabledCustomerPlan: bookingData.enabledCustomerPlan ?? false
        });

        setBooking(bookingData);
        setSelectedArea(bookingData.areas[0]);
        initializeState(floorPlansData, bookingData);
      }
    };
    fetch();
  }, [listingId]);

  useEffect(() => {
    setMode("select");
    clearSelectTransform(setSelectedIds, transformerRef);
    if (isManagementSection && selectedArea?.id === "Entrance") {
      setSelectedArea(booking.areas[0]);
    }
  }, [isManagementSection]);

  const initializeState = (floorPlansData, bookingData) => {
    const floorPlanTableIds = [];
    const customerPlanTablesId = [];
    const buttonIds = [];
    floorPlansData?.floorPlanData && Object.keys(floorPlansData.floorPlanData).forEach((areaId) => {
      const areaObject = floorPlansData.floorPlanData[areaId];

      areaObject.forEach((item) => {
        if (item.type === "table") {
          floorPlanTableIds.push(item.id);
        }
      });
    });
    floorPlansData?.customerPlanData && Object.keys(floorPlansData.customerPlanData).forEach((areaId) => {
      const areaObject = floorPlansData.customerPlanData[areaId];

      areaObject.forEach((item) => {
        if (item.type === "table") {
          customerPlanTablesId.push(item.id);
        }
        if (item.type === "button") {
          buttonIds.push(item.id)
        }
      });
    });

    const inventory = {};
    const customerInventory = {};
    bookingData.tables.forEach((table) => {
      const areaId = table.area.id;
      const capacity = table.capacity;
      if (!inventory[areaId]) inventory[areaId] = {};
      if (!customerInventory[areaId]) customerInventory[areaId] = {};
      if (!inventory[areaId][capacity]) inventory[areaId][capacity] = [];
      if (!customerInventory[areaId][capacity]) customerInventory[areaId][capacity] = [];
      if (!floorPlanTableIds.includes(table.id)) inventory[areaId][capacity].push(table);
      if (!customerPlanTablesId.includes(table.id)) customerInventory[areaId][capacity].push(table);
    });
    setTablesInventory(inventory);
    setCustomerTablesInventory(customerInventory);

    const areasInventory = {
      Entrance: [{
        id: `Welcome-${bookingData.id}`,
        name: 'Welcome',
        count: buttonIds.includes(`Entrance-Welcome-${bookingData.id}`) ? 0 : 1,
      }]
    };
    bookingData.areas.forEach((area) => {
      areasInventory["Entrance"].push({
        id: area.id,
        name: area.name,
        count: buttonIds.includes(`Entrance-${area.id}`) ? 0 : 1,
      });
      if (!areasInventory[area.id])
        areasInventory[area.id] = [
          { id: "Entrance", name: "Entrance", count: buttonIds.includes(`${area.id}-Entrance`) ? 0 : 1 },
        ];
      bookingData.areas.forEach((diffArea) => {
        if (
          diffArea.id !== area.id &&
          !areasInventory[area.id].find((a) => a.id === diffArea.id)
        ) {

          areasInventory[area.id].push({
            id: diffArea.id,
            name: diffArea.name,
            count: buttonIds.includes(`${area.id}-${diffArea.id}`) ? 0 : 1,
          });
        }
      });
    });
    setCustomerAreasInventory(areasInventory);

    floorPlansData?.floorPlanData && setFloorPlans(floorPlansData.floorPlanData);

    if (floorPlansData?.customerPlanData) {
      let updatedData = JSON.parse(JSON.stringify(floorPlansData.customerPlanData));
      for (let key in updatedData) {
        if (updatedData.hasOwnProperty(key)) {
          if (!updatedData[key].find(shape => shape.id.includes('mobile-viewport'))) {
            const mobileViewport = {
              x: 100,
              y: 100,
              width: 140,
              height: 300,
              id: `mobile-viewport-${key}${key === "Entrance" ? Date.now() : ''}`,
              type: 'viewport'
            };
            updatedData[key].push(mobileViewport);
          }
          if (!updatedData[key].find(shape => shape.id.includes('desktop-viewport'))) {
            const desktopViewport = {
              x: 100,
              y: 100,
              width: 300,
              height: 169,
              id: `desktop-viewport-${key}${key === "Entrance" ? Date.now() : ''}`,
              type: 'viewport'
            };
            updatedData[key].push(desktopViewport);
          }
        }
      }
      setCustomerPlans(updatedData);
    }
  }

  const addTableToPlan = (capacity) => {
    setInventory((prev) => {
      if (!prev[selectedArea?.id]?.[capacity]?.length) return prev;
      const updated = { ...prev };
      const tableToAdd = updated[selectedArea?.id][capacity].shift();
      let spawn = { x: 50, y: 50 };

      setPlans((prevPlans) => ({
        ...prevPlans,
        [selectedArea?.id]: [
          ...(prevPlans[selectedArea?.id] || []),
          {
            id: tableToAdd.id,
            type: "table",
            name: tableToAdd.name,
            capacity: tableToAdd.capacity,
            x: spawn.x,
            y: spawn.y,
            rotation: 0,
          },
        ],
      }));

      return updated;
    });
  };

  const addRectToPlan = (rect) => {
    setFloorPlans((prevPlans) => ({
      ...prevPlans,
      [selectedArea?.id]: [
        ...(prevPlans[selectedArea?.id] || []),
        {
          id: rect.id,
          type: "rect",
          x: rect.width > 0 ? rect.x : rect.x + rect.width,
          y: rect.height > 0 ? rect.y : rect.y + rect.height,
          width: Math.abs(rect.width),
          height: Math.abs(rect.height),
          rotation: 0,
        },
      ],
    }));
    setMode("select");
    setSelectedIds((prevIds) => {
      let newSelectedIds = [rect.id];

      setTimeout(() => {
        const nodes = newSelectedIds
          .map((sid) => shapeRefs.current[sid])
          .filter(Boolean);

        if (transformerRef.current && nodes.length > 0) {
          transformerRef.current.nodes(nodes);
          transformerRef.current.getLayer().batchDraw();
        }
      }, 0);

      return newSelectedIds;
    });
  };

  const addTextToPlan = (textShape) => {
    setFloorPlans((prevPlans) => ({
      ...prevPlans,
      [selectedArea?.id]: [
        ...(prevPlans[selectedArea?.id] || []),
        {
          id: textShape.id,
          type: "text",
          x: textShape.x,
          y: textShape.y,
          text: textShape.text || "Double click to change",
          fontSize: 14,
          width: textShape.width || 150,
          height: textShape.height || 40,
          rotation: 0,
        },
      ],
    }));

    setMode("select");
    setSelectedIds([textShape.id]);

    setTimeout(() => {
      const node = shapeRefs.current[textShape.id];
      if (transformerRef.current && node) {
        transformerRef.current.nodes([node]);
        transformerRef.current.getLayer().batchDraw();
      }
    }, 0);
  };

  const addButtonToPlan = (areaId) => {
    setCustomerAreasInventory((prev) => {
      const updated = { ...prev };
      const areaList = updated[selectedArea?.id];
      const areaToAdd = areaList.find((a) => a.id === areaId);

      if (!areaToAdd || areaToAdd.count <= 0) return prev;

      areaToAdd.count -= 1;

      setCustomerPlans((prevPlans) => ({
        ...prevPlans,
        [selectedArea?.id]: [
          ...(prevPlans[selectedArea?.id] || []),
          {
            id: `${selectedArea?.id}-${areaId}`, // unique ID
            type: "button",
            direction: "left",
            name: areaToAdd.name,
            x: 50,
            y: 50,
          },
        ],
      }));

      return updated;
    });
  };

  const toggleButton = (id, newDirection) => {
    setCustomerPlans((prev) => {
      const updated = { ...prev };

      if (!updated[selectedArea?.id]) return prev;

      updated[selectedArea?.id] = updated[selectedArea?.id].map((shape) =>
        shape.id === id ? { ...shape, direction: newDirection } : shape
      );

      return updated;
    });
  };

  const removeShapesFromPlan = () => {
    setPlans((prev) => ({
      ...prev,
      [selectedArea?.id]: prev[selectedArea?.id].filter(
        (shape) => !selectedIds.includes(shape.id)
      ),
    }));

    setInventory((prev) => {
      const tablesToReturn = booking?.tables.filter((table) =>
        selectedIds.includes(table.id)
      );
      if (!tablesToReturn || tablesToReturn.length === 0) return prev;

      const updatedInventory = { ...prev };

      tablesToReturn.forEach((table) => {
        const { capacity } = table;

        if (!updatedInventory[selectedArea?.id][capacity]) {
          updatedInventory[selectedArea?.id][capacity] = [];
        }

        updatedInventory[selectedArea?.id][capacity] = [
          ...updatedInventory[selectedArea?.id][capacity],
          table,
        ];
      });

      return updatedInventory;
    });

    setCustomerAreasInventory((prev) => {
      const updated = { ...prev };

      if (!updated[selectedArea?.id]) return prev;

      updated[selectedArea.id] = updated[selectedArea.id].map((area) => {
        if (selectedIds.find((id) => id.includes(area.id))) {
          return {
            ...area,
            count: 1,
          };
        }
        return area;
      });
      return updated;
    });
  };

  const addAllTables = () => {
    const areaId = selectedArea?.id;
    let spawn = { x: 50, y: -10 };

    setInventory((prevInventory) => {
      if (!prevInventory[areaId]) return prevInventory;
      const updatedInventory = { ...prevInventory };
      const tablesToAdd = [];

      Object.entries(updatedInventory[areaId]).forEach(([capacity, tables]) => {
        while (tables.length > 0) {
          const tableToAdd = tables.shift();
          tablesToAdd.push({ ...tableToAdd, capacity: Number(capacity) });
        }
      });

      updatedInventory[areaId] = Object.keys(updatedInventory[areaId]).reduce(
        (acc, cap) => {
          acc[cap] = [];
          return acc;
        },
        {}
      );

      setPlans((prevPlans) => {
        const verticalFitCount = Math.floor(stageSize.height / 80) - 1;
        let i = 0;

        const newTables = tablesToAdd.map((table) => {
          spawn.y += 80;
          i += 1;
          if (i % verticalFitCount === 0) {
            spawn.x += 230;
            spawn.y = 50;
          }
          return {
            id: table.id,
            type: "table",
            name: table.name,
            capacity: table.capacity,
            x: spawn.x,
            y: spawn.y,
            rotation: 0,
          };
        });

        return {
          ...prevPlans,
          [areaId]: [...(prevPlans[areaId] || []), ...newTables],
        };
      });

      return updatedInventory;
    });

    spawn.x += 230;
    spawn.y = 50;

    !isManagementSection &&
      setCustomerAreasInventory((prevInventory) => {
        const updated = { ...prevInventory };
        const areaList = updated[areaId];
        if (!areaList) return prevInventory;

        const buttonsToAdd = [];
        areaList.forEach((entry) => {
          if (entry.count > 0) {
            buttonsToAdd.push({
              id: `${areaId}-${entry.id}`,
              type: "button",
              name: entry.name,
              direction: "left",
              x: spawn.x,
              y: spawn.y,
            });
            entry.count = 0;
          }
          spawn.y += 80;
        });

        setCustomerPlans((prevPlans) => ({
          ...prevPlans,
          [areaId]: [...(prevPlans[areaId] || []), ...buttonsToAdd],
        }));

        return updated;
      });
  };

  const removeAllTables = () => {
    const areaId = selectedArea?.id;
    const toReturn = isManagementSection
      ? floorPlans[areaId]
      : customerPlans[areaId];
    if (!toReturn) return;

    clearSelectTransform(setSelectedIds, transformerRef);
    setPlans((prev) => ({ ...prev, [areaId]: [] }));
    setInventory((prev) => {
      const updated = { ...prev };
      toReturn.forEach((table) => {
        const cap = table.capacity;
        if (cap) {
          if (!updated[areaId][cap]) updated[areaId][cap] = [];
          updated[areaId][cap].push(table);
        }
      });
      return updated;
    });

    setCustomerAreasInventory((prev) => {
      const updated = { ...prev };

      if (!updated[selectedArea?.id]) return prev;

      updated[selectedArea.id] = updated[selectedArea.id].map((area) => {
        return {
          ...area,
          count: 1,
        };
      });
      return updated;
    });
  };

  const saveFloorPlan = async () => {
    try {
      await upsertFloorPlans({
        variables: {
          input: {
            bookingModuleId: booking.id,
            floorPlanData: floorPlans,
            customerPlanData: customerPlans,
            enabledCustomerPlan: isEnabledCustomerPlan
          },
        },
      });
      enqueueSnackbar(translate("messages.floorPlanSaveSuccessfully"));
    } catch (e) {
      enqueueSnackbar(translate("messages.floorPlanSaveError"), {
        variant: "error",
      });
    }
  };

  return {
    booking,
    selectedArea,
    setSelectedArea,
    tablesInventory,
    customerTablesInventory,
    customerAreasInventory,
    floorPlans,
    setFloorPlans,
    customerPlans,
    setCustomerPlans,
    selectedIds,
    setSelectedIds,
    mode,
    setMode,
    methods,
    viewportMethods,
    enabledPlanMethods,
    isManagementSection,
    isViewportMobile,
    isEnabledCustomerPlan,
    shapeRefs,
    transformerRef,
    imageTransformerRef,
    stageRef,
    layerRef,
    fileInputRef,
    addTableToPlan,
    addRectToPlan,
    addTextToPlan,
    addButtonToPlan,
    addAllTables,
    removeShapesFromPlan,
    removeAllTables,
    saveFloorPlan,
    toggleButton,
    canvasActions: canvasActions,
  };
};

export default useFloorPlanState;
